import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * ManageColumns — Component (L1)
 *
 * Column visibility manager — popover with checkboxes for each available
 * column. Persists per user in `localStorage`, scoped by `tableId`.
 *
 * Used by every backend-driven table in billing / claims / patient list /
 * appointment list / reports. Renders a button trigger that opens a small
 * popover listing every column with its visibility checkbox.
 *
 * Behaviour rules locked at the contract level:
 *  - `required=true` columns render as disabled+checked; cannot be hidden
 *  - `onChange` receives the visible-ID list, EXCLUDING required-but-
 *    unchecked (required is always treated as visible)
 *  - persistence key: `scrips:manage-columns:<tableId>` → string[] of
 *    visible IDs. Changing `tableId` invalidates user preferences.
 *
 * a11y: trigger is `button`; popover is `dialog`; checkbox group has
 * explicit label associations. Required columns carry `aria-disabled=true`.
 *
 * Scope: cross-module · Surfaces: practice · practitioner · patient
 * Contract: scrips-signal-ds/contracts/ManageColumns.json
 */

export interface ManageColumnsColumn {
  id: string;
  label: string;
  visible: boolean;
  required?: boolean;
}

export interface ManageColumnsProps {
  /** Stable identifier for localStorage persistence — e.g., 'patient-list'. Required. */
  tableId: string;
  /** Full column registry. `required=true` forces visible (e.g., primary identifier). */
  columns: ManageColumnsColumn[];
  /** Called when the user toggles. Receives the new visible-column id list. */
  onChange?: (visibleIds: string[]) => void;
  /** Optional trigger label override. Default: "Columns". */
  triggerLabel?: string;
  className?: string;
}

const STORAGE_PREFIX = 'scrips:manage-columns:';

function readPersisted(tableId: string): string[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + tableId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : null;
  } catch {
    return null;
  }
}

function writePersisted(tableId: string, ids: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + tableId, JSON.stringify(ids));
  } catch {
    // localStorage may be unavailable (private mode, quota exceeded) — silently no-op
  }
}

function resolveInitial(tableId: string, columns: ManageColumnsColumn[]): Set<string> {
  const persisted = readPersisted(tableId);
  const initial = new Set<string>();
  for (const col of columns) {
    if (col.required) {
      initial.add(col.id);
      continue;
    }
    if (persisted) {
      if (persisted.includes(col.id)) initial.add(col.id);
    } else if (col.visible) {
      initial.add(col.id);
    }
  }
  return initial;
}

export function ManageColumns({
  tableId,
  columns,
  onChange,
  triggerLabel = 'Columns',
  className,
}: ManageColumnsProps) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState<Set<string>>(() => resolveInitial(tableId, columns));
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const handlePointer = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const toggle = (col: ManageColumnsColumn) => {
    if (col.required) return; // guard: required cannot be hidden
    const next = new Set(visible);
    if (next.has(col.id)) next.delete(col.id);
    else next.add(col.id);
    setVisible(next);
    const ids = columns.filter((c) => next.has(c.id) || c.required).map((c) => c.id);
    writePersisted(tableId, ids);
    onChange?.(ids);
  };

  return (
    <div className={cn('relative inline-block', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-2 rounded-md border border-grey-200 bg-white px-3 py-1.5 text-sm font-medium text-grey-900 transition-colors',
          'hover:bg-grey-100 active:bg-grey-100',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2',
        )}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M2 3.5h10M2 7h10M2 10.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        {triggerLabel}
      </button>
      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label="Manage columns"
          className={cn(
            'absolute right-0 top-full z-20 mt-1 min-w-[224px] rounded-md border border-grey-200 bg-white shadow-md',
          )}
        >
          <div className="border-b border-grey-200 px-3 py-1.5 text-xs font-medium text-grey-700">
            Columns
          </div>
          <ul className="flex flex-col gap-1 p-3">
            {columns.map((col) => {
              const isVisible = visible.has(col.id) || !!col.required;
              const inputId = `manage-columns-${tableId}-${col.id}`;
              return (
                <li key={col.id}>
                  <label
                    htmlFor={inputId}
                    className={cn(
                      'flex items-center gap-2 rounded px-2 py-1 text-sm text-grey-900',
                      col.required ? 'cursor-not-allowed text-grey-700' : 'cursor-pointer hover:bg-grey-100',
                    )}
                  >
                    <input
                      id={inputId}
                      type="checkbox"
                      checked={isVisible}
                      disabled={!!col.required}
                      aria-disabled={col.required || undefined}
                      onChange={() => toggle(col)}
                      className={cn(
                        'h-4 w-4 rounded border-grey-300 text-blue-500',
                        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-1',
                      )}
                    />
                    <span className="flex-1">{col.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
